import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import TableContainer from "components/Common/TableContainer"

interface ProductRow {
  id: string
  product: string
  category: string
  couponBased: "Coupon Based" | "One Time"
  salesVolume: string
  orders: number
  piece: number
  status: "Active" | "In Active"
}

interface TopProductsBySellTableProps {
  products: ProductRow[]
  isAllChecked: boolean
  checkedProducts: ProductRow[]
  toggleAllProducts: () => void
  togglecheckedProductHandler: (product: ProductRow) => void
  toggleActiveProduct: (product: ProductRow) => void
}

export const TopProductsBySellTable: React.FC<TopProductsBySellTableProps> = ({
  products,
  isAllChecked,
  checkedProducts,
  toggleAllProducts,
  togglecheckedProductHandler,
  toggleActiveProduct,
}) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <input
            type="checkbox"
            className="form-check-input"
            onClick={toggleAllProducts}
            checked={isAllChecked}
          />
        ),
        accessor: "id",
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return (
            <input
              onClick={() =>
                togglecheckedProductHandler(cellProps.row.original)
              }
              checked={Boolean(
                checkedProducts.find(p => p.id === cellProps.row.original.id)
              )}
              type="checkbox"
              className="form-check-input"
            />
          )
        },
      },
      {
        Header: t("products_products"),
        accessor: "product",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return (
            <div className="d-flex justify-content-start align-items-center gap-3">
              <img
                src="https://placehold.co/30x30/png"
                alt="avatar"
                className="rounded-circle"
                style={{
                  width: "36px",
                  height: "36px",
                }}
              />
              <p className="mb-0">{cellProps.row.original.product}</p>
            </div>
          )
        },
      },
      {
        Header: t("products_category"),
        accessor: "category",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return <>{cellProps.row.original.category}</>
        },
      },
      {
        Header: t("products_couponBased"),
        accessor: "couponBased",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return (
            <>
              {cellProps.row.original.couponBased === "Coupon Based" ? (
                <span className="badge fw-semibold bg-primary-30 text-primary me-1 px-2 py-1 font-size-10">
                  {t("products_couponBased")}
                </span>
              ) : (
                <span className="badge fw-semibold bg-cyan-30 text-cyan me-1 px-2 py-1 font-size-10">
                  {t("products_oneTime")}
                </span>
              )}
            </>
          )
        },
      },
      {
        Header: t("products_salesVolume"),
        accessor: "salesVolume",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return <>{cellProps.row.original.salesVolume}</>
        },
      },
      {
        Header: t("products_orders"),
        accessor: "orders",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return <>{cellProps.row.original.orders}</>
        },
      },
      {
        Header: t("dashboard_piece"),
        accessor: "piece",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return <>{cellProps.row.original.piece}</>
        },
      },
      {
        Header: t("common_status"),
        accessor: "status",
        filterable: true,
        Cell: (cellProps: { row: { original: ProductRow } }) => {
          return (
            <div className="form-check form-switch mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id={`customSwitch${cellProps.row.original.product}`}
                checked={cellProps.row.original.status === "Active"}
                onClick={() => toggleActiveProduct(cellProps.row.original)}
              />
              <label
                className="form-check-label text-capitalize"
                htmlFor={`customSwitch${cellProps.row.original.product}`}
              >
                {cellProps.row.original.status}
              </label>
            </div>
          )
        },
      },
    ],
    [products, t, isAllChecked, checkedProducts, toggleAllProducts, togglecheckedProductHandler, toggleActiveProduct]
  )

  return (
    <TableContainer
      columns={columns}
      data={products}
      isGlobalFilter={true}
      isAddOptions={false}
      customPageSize={5}
      isJobListGlobalFilter={undefined}
      isAddUserList={undefined}
      handleOrderClicks={undefined}
      handleUserClick={undefined}
      handleCustomerClick={undefined}
      isAddCustList={undefined}
      className={undefined}
      customPageSizeOptions={undefined}
    />
  )
}